// packages
import cx from 'classnames';
import { get, set } from '../vendor/idb-keyval';
import { h } from 'preact';

// components
import CandidateRow from './CandidateRow';
import Contest from './Contest';
import SeatPartyDivider from './SeatPartyDivider';
import Portal from './Portal';
import PureComponent from './PureComponent';
import Typeahead from './Typeahead';

// internal
import debounce from '../utils/debounce';
import { determineContestTitleYourRaces } from '../election-utils';
import { visibleCandidatesCount } from '../endpoints';
import { lookup } from '../utils/legislative-districts';
import loadAssetURL from '../utils/load-asset-url';

const googleIMG = loadAssetURL('powered-by-google');

let PlacesLib = null; // { AutocompleteSessionToken, AutocompleteSuggestion, Place } loaded via importLibrary()

const bounds = {
  east: -93.514764,
  west: -106.662177,
  south: 25.843902,
  north: 36.500199,
};

const toLocationRestriction = b => ({
  west: b.west,
  east: b.east,
  south: b.south,
  north: b.north,
});

const getPredictions = async (input, sessionToken) => {
  if (!input || input.trim().length < 2) return [];

  const request = {
    input,
    locationRestriction: toLocationRestriction(bounds),
    includedPrimaryTypes: ['street_address', 'premise', 'route'],
    region: 'us',
    sessionToken,
  };

  const {
    suggestions,
  } = await PlacesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
    request
  );

  return suggestions.map(s => ({
    id: s.placePrediction.placeId,
    description: s.placePrediction.text.toString(),
    place_id: s.placePrediction.placeId,
    _prediction: s.placePrediction,
  }));
};

// always render what's provided, the geocoder decides
const shouldItemRender = () => true;

class YourRaces extends PureComponent {
  constructor() {
    super();

    this.state = {
      contests: [],
      coordinates: null,
      data: null,
      lookupError: false,
      lookupPending: false,
      lookedFoundStored: false,
      redirectFish: false,
      results: [],
      value: '',
      // default placeholder
      placeholder: '1010 Colorado St, Austin, TX, 78701',
    };

    this.handleGeocode = this.handleGeocode.bind(this);
    this.debouncedHandleGeocode = debounce(this.handleGeocode, 300);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.sessionToken = null;
  }

  onClear(value) {
    this.setState({ value: '' });
  }

  onChange(value) {
    this.setState({ value }, this.debouncedHandleGeocode);
  }

  async handleGeocode() {
    if (!PlacesLib) return;
    const { value: input } = this.state;
    try {
      const results = await getPredictions(input, this.sessionToken);
      this.setState({ results });
    } catch (e) {
      this.setState({ results: [] });
    }
  }

  async onSelect({ name, placeId, _prediction }) {
    this.setState({ lookupError: false, lookupPending: true, value: name });

    try {
      // New flow: prediction -> Place -> fetchFields({ fields: ['location'] })
      const place = _prediction
        ? _prediction.toPlace()
        : new PlacesLib.Place({ id: placeId });

      await place.fetchFields({ fields: ['location'] }); // ends the session

      const latLng = place.location;
      const coordinates = {
        latitude: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
        longitude: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
      };

      // Reset session so the next keystroke starts a fresh token
      this.sessionToken = null;

      lookup(coordinates)
        .then(({ data }) => {
          this.setState({
            coordinates,
            data,
            lookupPending: false,
            lookedFoundStored: false,
          });
        })
        .catch(() =>
          this.setState({
            data: null,
            lookupError: true,
            lookupPending: false,
          })
        );
    } catch (e) {
      this.setState({
        data: null,
        lookupError: true,
        lookupPending: false,
      });
      this.sessionToken = null;
    }
  }

  async componentDidMount() {
    // New: load the modern Places library classes
    // eslint-disable-next-line no-undef
    PlacesLib = await google.maps.importLibrary('places');

    // // If we ever wanted to show a default location, coordinates for Governor mansion in Austin
    // let latitude = 30.2730665;
    // let longitude = -97.74341629999999;
    // let coordinates = { latitude, longitude };

    // lookup(coordinates)
    //   .then(({ data }) => {
    //     this.setState({
    //       coordinates,
    //       data,
    //       lookupPending: false,
    //       lookedFoundStored: false,
    //     });
    //   })
    //   .catch(() =>
    //     this.setState({
    //       data: null,
    //       lookupError: true,
    //       lookupPending: false,
    //     })
    //   );  }
  }

  render() {
    const { contests, notes, annotations, asOf } = this.props;
    const {
      coordinates,
      data,
      lookupError,
      lookupPending,
      lookedFoundStored,
      results,
      value,
      placeholder,
      redirectFish,
    } = this.state;

    let addressFeedback = null;

    const matches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    const noteMatches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    const annoMatches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    if (data) {
      matches['us-house'] = contests.filter(
        contest =>
          contest.office === 'U.S. House' &&
          contest.office_division === data.us_house.district
      );

      matches['sboe'] = contests.filter(
        contest =>
          contest.office === 'State Board of Education' &&
          contest.office_division === data.sboe.district
      );

      matches['tx-senate'] = contests.filter(
        contest =>
          contest.office === 'Texas Senate' &&
          contest.office_division === data.tx_senate.district
      );

      matches['tx-house'] = contests.filter(
        contest =>
          contest.office === 'Texas House' &&
          contest.office_division === data.tx_house.district
      );

      noteMatches['us-house'] = notes.filter(
        contest =>
          contest.type ===
            'us-house-district-' + data.us_house.district + '-Democrat' ||
          contest.type ===
            'us-house-district-' + data.us_house.district + '-Republican'
      );

      noteMatches['sboe'] = notes.filter(
        contest =>
          contest.type ===
            'state-board-of-education-district-' +
              data.sboe.district +
              '-Democrat' ||
          contest.type ===
            'state-board-of-education-district-' +
              data.sboe.district +
              '-Republican'
      );

      noteMatches['tx-senate'] = notes.filter(
        contest =>
          contest.type ===
            'texas-senate-district-' + data.tx_senate.district + '-Democrat' ||
          contest.type ===
            'texas-senate-district-' + data.tx_senate.district + '-Republican'
      );

      noteMatches['tx-house'] = notes.filter(
        contest =>
          contest.type ===
            'texas-house-district-' + data.tx_house.district + '-Democrat' ||
          contest.type ===
            'texas-house-district-' + data.tx_house.district + '-Republican'
      );

      annoMatches['us-house'] = annotations.filter(
        contest =>
          contest.type ===
            'us-house-district-' + data.us_house.district + '-Democrat' ||
          contest.type ===
            'us-house-district-' + data.us_house.district + '-Republican'
      );

      annoMatches['sboe'] = annotations.filter(
        contest =>
          contest.type ===
            'state-board-of-education-district-' +
              data.sboe.district +
              '-Democrat' ||
          contest.type ===
            'state-board-of-education-district-' +
              data.sboe.district +
              '-Republican'
      );

      annoMatches['tx-senate'] = annotations.filter(
        contest =>
          contest.type ===
            'texas-senate-district-' + data.tx_senate.district + '-Democrat' ||
          contest.type ===
            'texas-senate-district-' + data.tx_senate.district + '-Republican'
      );

      annoMatches['tx-house'] = annotations.filter(
        contest =>
          contest.type ===
            'texas-house-district-' + data.tx_house.district + '-Democrat' ||
          contest.type ===
            'texas-house-district-' + data.tx_house.district + '-Republican'
      );
    }

    if (lookupPending) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Finding your details...{' '}
          <svg class="icon icon-spinner animate-pulse">
            <use xlinkHref="#icon-spinner" />
          </svg>
        </p>
      );
    }

    if (lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Hmm... we were unable to find you in Texas. Please check your address
          and try again!
        </p>
      );
    }

    if ((coordinates && !lookupPending && !lookupError) || redirectFish) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          We found your information! These races have been personalized for you.
        </p>
      );
    }

    if (lookedFoundStored && !lookupPending && !lookupError && !redirectFish) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Welcome back! We remembered the districts you found last time.
        </p>
      );
    }

    const inputProps = {
      id: 'address-input',
      class: 'address-input__input',
      placeholder: placeholder,
    };

    function findIsCalled(party, matches) {
      const race = matches.filter(race => race.party === party);
      return race[0].is_called;
    }

    function findContestResult(party, matches) {
      const race = matches.filter(race => race.party === party);
      return race[0].contest_result;
    }

    return (
      <div class="address-input t-sans">
        <svg class="icon icon-drawn-left-arrow">
          <use xlinkHref="#icon-drawn-left-arrow"></use>
        </svg>
        <label class="address-input__label" for="address-input">
          Enter your address
        </label>
        <Typeahead
          inputProps={inputProps}
          items={results}
          getItemValue={item => ({
            name: item.description,
            placeId: item.place_id,
            _prediction: item._prediction,
          })}
          value={value}
          onChange={this.onChange}
          onClear={this.onClear}
          onSelect={this.onSelect}
          renderItem={(item, isHighlighted) => (
            <li
              class={cx('listbox__item', {
                'listbox__item--highlighted': isHighlighted,
              })}
              key={item.id}
            >
              {item.description}
            </li>
          )}
          renderAttribution={() => (
            <li class="listbox__item listbox__item--attribution">
              <img src={googleIMG} alt="Powered by Google" />
            </li>
          )}
          shouldItemRender={shouldItemRender}
          renderInput={props => (
            <input
              {...props}
              ref={el => {
                props.ref(el);
                this.inputRef = el;
              }}
            />
          )}
        />
        {addressFeedback}
        {data && contests.length > 0 && (
          <Portal
            node={document.getElementById('your-races-results')}
            ref={ref => (this._resultsPortal = ref)}
          >
            <div class="representative-districts contest-list">
              <Contest
                key={matches['us-house'][0].office_slug}
                contest={matches['us-house']}
                contestTitle={determineContestTitleYourRaces(
                  matches['us-house'][0]
                )}
                tableHeader={CandidateRow.Header}
                columnCount={CandidateRow.columnCount}
                renderParty={party => (
                  <SeatPartyDivider
                    partyName={party.party}
                    partyBallot={party}
                    tableHeader={CandidateRow.Header}
                    annotations={annoMatches['us-house'].filter(
                      contest =>
                        contest.type === party.office_slug + '-' + party.party
                    )}
                    notes={noteMatches['us-house'].filter(
                      contest =>
                        contest.type === party.office_slug + '-' + party.party
                    )}
                    source={party.source}
                    asOf={asOf}
                    columnCount={CandidateRow.columnCount}
                    renderCandidate={(
                      candidate,
                      idx,
                      viewAllCandidates,
                      { totalVotes }
                    ) => (
                      <CandidateRow
                        key={candidate.id}
                        ballotName={candidate.name}
                        candidateId={candidate.id}
                        contestIsCalled={findIsCalled(
                          candidate.party,
                          matches['us-house']
                        )}
                        isIncumbent={candidate.is_incumbent}
                        contestResult={findContestResult(
                          candidate.party,
                          matches['us-house']
                        )}
                        candidatesNum={party.candidacies.length}
                        isWinner={candidate.is_winner}
                        isViewable={
                          viewAllCandidates || idx < visibleCandidatesCount
                        }
                        party={candidate.party}
                        totalVotesInContest={totalVotes}
                        votes={candidate.votes}
                      />
                    )}
                  />
                )}
              />
              {matches['tx-senate'].length > 0 && (
                <Contest
                  key={matches['tx-senate'].id}
                  contest={matches['tx-senate']}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitleYourRaces(
                    matches['tx-senate'][0]
                  )}
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annoMatches['tx-senate'].filter(
                        contest =>
                          contest.type === party.office_slug + '-' + party.party
                      )}
                      notes={noteMatches['tx-senate'].filter(
                        contest =>
                          contest.type === party.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={findIsCalled(
                            candidate.party,
                            matches['tx-senate']
                          )}
                          contestResult={findContestResult(
                            candidate.party,
                            matches['tx-senate']
                          )}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              )}
              <Contest
                key={matches['tx-house'].id}
                contest={matches['tx-house']}
                tableHeader={CandidateRow.Header}
                columnCount={CandidateRow.columnCount}
                contestTitle={determineContestTitleYourRaces(
                  matches['tx-house'][0]
                )}
                renderParty={party => (
                  <SeatPartyDivider
                    partyName={party.party}
                    partyBallot={party}
                    tableHeader={CandidateRow.Header}
                    annotations={annoMatches['tx-house'].filter(
                      contest =>
                        contest.type === party.office_slug + '-' + party.party
                    )}
                    notes={noteMatches['tx-house'].filter(
                      contest =>
                        contest.type === party.office_slug + '-' + party.party
                    )}
                    source={party.source}
                    asOf={asOf}
                    columnCount={CandidateRow.columnCount}
                    renderCandidate={(
                      candidate,
                      idx,
                      viewAllCandidates,
                      { totalVotes }
                    ) => (
                      <CandidateRow
                        key={candidate.id}
                        ballotName={candidate.name}
                        candidateId={candidate.id}
                        contestIsCalled={findIsCalled(
                          candidate.party,
                          matches['tx-house']
                        )}
                        contestResult={findContestResult(
                          candidate.party,
                          matches['tx-house']
                        )}
                        candidatesNum={party.candidacies.length}
                        isIncumbent={candidate.is_incumbent}
                        isViewable={
                          viewAllCandidates || idx < visibleCandidatesCount
                        }
                        isWinner={candidate.is_winner}
                        party={candidate.party}
                        totalVotesInContest={totalVotes}
                        votes={candidate.votes}
                      />
                    )}
                  />
                )}
              />
              {matches['sboe'].length > 0 && (
                <Contest
                  key={matches['sboe'][0].office_slug}
                  contest={matches['sboe']}
                  tableHeader={CandidateRow.Header}
                  columnCount={CandidateRow.columnCount}
                  contestTitle={determineContestTitleYourRaces(
                    matches['sboe'][0]
                  )}
                  renderParty={party => (
                    <SeatPartyDivider
                      partyName={party.party}
                      partyBallot={party}
                      tableHeader={CandidateRow.Header}
                      annotations={annoMatches['sboe'].filter(
                        contest =>
                          contest.type === party.office_slug + '-' + party.party
                      )}
                      notes={noteMatches['sboe'].filter(
                        contest =>
                          contest.type === party.office_slug + '-' + party.party
                      )}
                      source={party.source}
                      asOf={asOf}
                      columnCount={CandidateRow.columnCount}
                      renderCandidate={(
                        candidate,
                        idx,
                        viewAllCandidates,
                        { totalVotes }
                      ) => (
                        <CandidateRow
                          key={candidate.id}
                          ballotName={candidate.name}
                          candidateId={candidate.id}
                          contestIsCalled={findIsCalled(
                            candidate.party,
                            matches['sboe']
                          )}
                          contestResult={findContestResult(
                            candidate.party,
                            matches['sboe']
                          )}
                          candidatesNum={party.candidacies.length}
                          isIncumbent={candidate.is_incumbent}
                          isWinner={candidate.is_winner}
                          isViewable={
                            viewAllCandidates || idx < visibleCandidatesCount
                          }
                          party={candidate.party}
                          totalVotesInContest={totalVotes}
                          votes={candidate.votes}
                        />
                      )}
                    />
                  )}
                />
              )}
            </div>
          </Portal>
        )}
      </div>
    );
  }
}

export default YourRaces;
